import type { components } from "../../types/api";
import { apiFetch, toQuery } from "../../lib/api";

export type ClientList = components["schemas"]["ClientList"];
export type ClientSummary = components["schemas"]["ClientSummary"];
export type ClientDetail = components["schemas"]["ClientDetail"];
export type ClientUpdate = components["schemas"]["ClientUpdate"];
export type ClientNote = components["schemas"]["ClientNoteOut"];
export type TagOut = components["schemas"]["TagOut"];
export type ClientLanguageInfo = components["schemas"]["ClientLanguageInfo"];
export type ClientLanguage = "ka" | "en";
export type PosSalesPage = components["schemas"]["PosSalesPage"];

export type ClientStatus = "prospect" | "member" | "lapsed" | "flagged";
export type ClientSort =
  | "name"
  | "-name"
  | "last_visit"
  | "-last_visit"
  | "lifetime"
  | "-lifetime"
  | "loyalty"
  | "-loyalty"
  | "visits"
  | "-visits"
  | "renewals"
  | "-renewals"
  | "last_comm"
  | "-last_comm";

export type LastCommWithin = "7" | "30" | "90" | "over90" | "never";

export type ClientsSearchParams = {
  q?: string;
  status?: ClientStatus;
  tag?: string[];
  language?: ClientLanguage[];
  tier?: string[];
  cycle?: string[];
  membership?: string[];
  page?: number;
  page_size?: number;
  sort?: ClientSort;
  last_visit_within?: "7" | "30" | "90" | "lapsed";
  last_comm_within?: LastCommWithin;
  spend_min?: number;
  spend_max?: number;
  min_renewals?: number;
  hide_hotel_guests?: boolean;
  favorites?: boolean;
};

export function searchToParams(s: ClientsSearchParams): URLSearchParams {
  return toQuery({
    q: s.q,
    status: s.status,
    tag: s.tag,
    language: s.language,
    tier: s.tier,
    cycle: s.cycle,
    membership: s.membership,
    page: s.page ?? 1,
    page_size: s.page_size ?? 50,
    sort: s.sort ?? "name",
    last_visit_within: s.last_visit_within,
    last_comm_within: s.last_comm_within,
    spend_min: s.spend_min,
    spend_max: s.spend_max,
    min_renewals: s.min_renewals,
    // Default is to hide comp/hotel guests; only send the flag when showing them.
    hide_hotel_guests: s.hide_hotel_guests === false ? "false" : undefined,
    favorites: s.favorites,
  });
}

export const listClients = (s: ClientsSearchParams) =>
  apiFetch<ClientList>(`/api/clients?${searchToParams(s)}`);

export const getClient = (id: string) => apiFetch<ClientDetail>(`/api/clients/${id}`);

export type ClientStatsSummary = components["schemas"]["ClientStatsSummary"];

export const getClientStats = () => apiFetch<ClientStatsSummary>("/api/clients/stats");

export type ClientInsights = components["schemas"]["ClientInsightsOut"];

export const getClientInsights = (id: string, months = 24) =>
  apiFetch<ClientInsights>(`/api/clients/${id}/insights?months=${months}`);

export type ClientVisitActivity = components["schemas"]["ClientVisitActivity"];
export type ClientVisitLog = components["schemas"]["ClientVisitLog"];
export type ClientVisitDay = ClientVisitLog["items"][number];

export const getClientVisits = (id: string) =>
  apiFetch<ClientVisitActivity>(`/api/clients/${id}/visits`);

export const listClientVisitLog = (id: string, limit = 50, offset = 0) =>
  apiFetch<ClientVisitLog>(`/api/clients/${id}/visits/log?limit=${limit}&offset=${offset}`);

export const patchClient = (id: string, body: ClientUpdate) =>
  apiFetch<ClientDetail>(`/api/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

/** Staff set the member's language; the name-based guess never changes it again. */
export const setClientLanguage = (id: string, language: ClientLanguage) =>
  apiFetch<ClientLanguageInfo>(`/api/clients/${id}/language`, {
    method: "PUT",
    body: JSON.stringify({ language }),
  });

export const deleteClient = (id: string) =>
  apiFetch<void>(`/api/clients/${id}`, { method: "DELETE" });

export const addTag = (clientId: string, name: string) =>
  apiFetch<TagOut>(`/api/clients/${clientId}/tags`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const removeTag = (clientId: string, tagId: string) =>
  apiFetch<void>(`/api/clients/${clientId}/tags/${tagId}`, { method: "DELETE" });

// Per-user favorite stars: both calls are idempotent.
export const favoriteClient = (clientId: string) =>
  apiFetch<void>(`/api/clients/${clientId}/favorite`, { method: "PUT" });

export const unfavoriteClient = (clientId: string) =>
  apiFetch<void>(`/api/clients/${clientId}/favorite`, { method: "DELETE" });

export const listNotes = (clientId: string) =>
  apiFetch<ClientNote[]>(`/api/clients/${clientId}/notes`);

export const createNote = (clientId: string, body: string) =>
  apiFetch<ClientNote>(`/api/clients/${clientId}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });

export const updateNote = (clientId: string, noteId: string, body: string) =>
  apiFetch<ClientNote>(`/api/clients/${clientId}/notes/${noteId}`, {
    method: "PATCH",
    body: JSON.stringify({ body }),
  });

export const deleteNote = (clientId: string, noteId: string) =>
  apiFetch<void>(`/api/clients/${clientId}/notes/${noteId}`, { method: "DELETE" });

export const listClientPosSales = (clientId: string, limit = 50, offset = 0) =>
  apiFetch<PosSalesPage>(`/api/clients/${clientId}/pos-sales?limit=${limit}&offset=${offset}`);

// ── Momentum (Clients → Momentum tab) ──
export type MomentumList = components["schemas"]["MomentumList"];
export type MomentumItem = components["schemas"]["MomentumItem"];
export type MomentumSignal = MomentumItem["signal"];
export type MomentumSort = "score" | "-score" | "progress" | "-progress" | "name" | "-name";

export type MomentumSearchParams = {
  signal?: MomentumSignal;
  almost_there?: boolean;
  sort?: MomentumSort;
  page?: number;
  page_size?: number;
};

export const listMomentum = (s: MomentumSearchParams) => {
  const qs = toQuery({
    signal: s.signal,
    almost_there: s.almost_there,
    sort: s.sort ?? "-score",
    page: s.page ?? 1,
    page_size: s.page_size ?? 50,
  });
  return apiFetch<MomentumList>(`/api/clients/momentum?${qs}`);
};

// ── Loyalty tier rules (Gold/Silver/Bronze bands) ──
export type TierRuleOut = components["schemas"]["TierRuleOut"];
export type TierRuleCreate = components["schemas"]["TierRuleCreate"];
export type TierRuleUpdate = components["schemas"]["TierRuleUpdate"];

export const listTierRules = () => apiFetch<TierRuleOut[]>("/api/clients/tier-rules");

export const createTierRule = (body: TierRuleCreate) =>
  apiFetch<TierRuleOut>("/api/clients/tier-rules", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateTierRule = (id: string, body: TierRuleUpdate) =>
  apiFetch<TierRuleOut>(`/api/clients/tier-rules/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteTierRule = (id: string) =>
  apiFetch<void>(`/api/clients/tier-rules/${id}`, { method: "DELETE" });

export type MembershipTimeline = components["schemas"]["MembershipTimelineResponse"];
export type MembershipStretch = components["schemas"]["MembershipTimelineStretch"];

/** Every membership the client held, grouped into unbroken stretches. */
export const getClientMembershipTimeline = (id: string) =>
  apiFetch<MembershipTimeline>(`/api/clients/${id}/membership-timeline`);
